import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Mail, Phone, Calendar, Download, ChevronRight, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import api from '@/lib/api';
import { formatDate } from '@/lib/format';

const statusColor = (s) => ({
  new: 'bg-[#C6A35B] text-[#144726]',
  contacted: 'bg-blue-100 text-blue-800',
  qualified: 'bg-teal-100 text-teal-800',
  closed: 'bg-slate-200 text-slate-700',
}[s] || 'bg-slate-100 text-slate-700');

export default function AdminLeadsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('new');
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    const params = filter !== 'all' ? { status: filter } : {};
    api.get('/leads', { params }).then((res) => setItems(res.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const openLead = (l) => { setSelected(l); setNotes(l.notes || ''); setStatus(l.status); };

  const updateLead = async () => {
    try {
      await api.put(`/leads/${selected.id}`, { status, notes });
      toast.success('Lead updated');
      setSelected(null);
      load();
    } catch { toast.error('Update failed'); }
  };

  const onDelete = async () => {
    try { await api.delete(`/leads/${deleteId}`); toast.success('Deleted'); setDeleteId(null); load(); }
    catch { toast.error('Delete failed'); }
  };

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Company', 'Property Type', 'Requirement', 'Source', 'Status', 'Created', 'Message', 'Notes'];
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = items.map((l) => [l.name, l.email, l.phone, l.company, l.property_type, l.requirement, l.source_page, l.status, l.created_at, l.message, l.notes].map(escape).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `cespedis-leads-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-leads-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#144726]">Leads</h1>
          <p className="mt-1 text-sm text-slate-500">All enquiries submitted via the public contact and property forms.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="admin-leads-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="qualified">Qualified</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={exportCSV} variant="outline" className="border-[#144726] text-[#144726]" data-testid="admin-leads-export"><Download className="mr-2 h-4 w-4" /> Export CSV</Button>
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600"><tr>
              <th className="px-4 py-3 text-left font-semibold">Lead</th>
              <th className="px-4 py-3 text-left font-semibold">Contact</th>
              <th className="px-4 py-3 text-left font-semibold">Interest</th>
              <th className="px-4 py-3 text-left font-semibold">Status</th>
              <th className="px-4 py-3 text-left font-semibold">Received</th>
              <th className="px-4 py-3 text-right font-semibold">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={6} className="px-4 py-6 text-slate-500">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-slate-500">No leads in this view.</td></tr>}
              {items.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openLead(l)} data-testid="admin-leads-row">
                  <td className="px-4 py-3"><p className="font-semibold text-[#144726]">{l.name}</p>{l.company && <p className="text-xs text-slate-500">{l.company}</p>}</td>
                  <td className="px-4 py-3 text-slate-600"><div className="flex flex-col gap-1"><span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5 text-[#C6A35B]" />{l.email}</span><span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-[#C6A35B]" />{l.phone}</span></div></td>
                  <td className="px-4 py-3 text-slate-600">{l.property_type || '—'}{l.requirement ? <p className="text-xs text-slate-500">{l.requirement.replace(/_/g, ' ')}</p> : null}</td>
                  <td className="px-4 py-3"><Badge className={`rounded-md border-0 ${statusColor(l.status)}`}>{l.status}</Badge></td>
                  <td className="px-4 py-3 text-slate-600"><span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {formatDate(l.created_at)}</span></td>
                  <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" className="border-slate-200" onClick={(e) => { e.stopPropagation(); openLead(l); }}>View <ChevronRight className="ml-1 h-3 w-3" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full max-w-md bg-white overflow-y-auto">
          {selected && (
            <div className="pt-6">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C6A35B]">Lead details</p>
              <h2 className="mt-2 font-display text-2xl font-semibold text-[#144726]">{selected.name}</h2>
              {selected.company && <p className="text-sm text-slate-500">{selected.company}</p>}
              <div className="mt-5 space-y-3 text-sm text-slate-700">
                <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#C6A35B]" /> {selected.email}</p>
                <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#C6A35B]" /> {selected.phone}</p>
                <p className="flex items-center gap-2"><Calendar className="h-4 w-4 text-[#C6A35B]" /> {formatDate(selected.created_at)}</p>
                {selected.property_type && <p><span className="text-slate-500">Property type:</span> {selected.property_type}</p>}
                {selected.requirement && <p><span className="text-slate-500">Requirement:</span> {selected.requirement.replace(/_/g, ' ')}</p>}
                {selected.source_page && <p><span className="text-slate-500">Source:</span> {selected.source_page}</p>}
              </div>
              {selected.message && (<div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4"><p className="text-xs uppercase tracking-wider text-slate-500">Message</p><p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{selected.message}</p></div>)}

              <div className="mt-6 space-y-3">
                <div><Label>Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid="admin-lead-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="qualified">Qualified</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Notes</Label><Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes…" /></div>
              </div>
              <div className="mt-6 flex items-center gap-2">
                <Button onClick={updateLead} className="flex-1 bg-[#144726] text-white hover:bg-[#0E3520]" data-testid="admin-lead-save">Save changes</Button>
                <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => setDeleteId(selected.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete lead?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
